import * as THREE from "three";
import { drawCard, drawCardBack } from "../items/cardArt.js";

/**
 * Lightweight 3D playing-card animator for the poker table. Cards are SMALL planes that fly from the
 * dealer; community cards land FLAT FACE-UP on the felt, while hole cards HOVER in the air in front of
 * each player with a gentle flowing bob, facing that player. Each client renders only ITS OWN hole
 * cards face-up (others' stay face-down). Parented to the casino zone group so they hide with the zone.
 */

const CARD_W = 0.16, CARD_H = 0.224;

interface Card {
  mesh: THREE.Mesh;
  from: THREE.Vector3;
  to: THREE.Vector3;                 // rest position
  faceToward: THREE.Vector3 | null;  // set ⇒ hovers upright facing this point (a player); else lies flat
  flying: boolean;
  t: number;
  dur: number;
  phase: number;                     // per-card phase so they don't bob in lock-step
  restClock: number;                 // ms since landing — drives a small DECAYING settle (not a loop)
}

export class Cards3D {
  private readonly cards: Card[] = [];
  private backTex: THREE.CanvasTexture | null = null;
  private clock = 0;
  private phaseSeed = 0;

  constructor(private readonly parent: THREE.Object3D) {}

  private cardBack(): THREE.CanvasTexture {
    if (!this.backTex) {
      this.backTex = new THREE.CanvasTexture(drawCardBack());
      this.backTex.magFilter = THREE.NearestFilter;
      this.backTex.minFilter = THREE.NearestFilter;
      this.backTex.colorSpace = THREE.SRGBColorSpace;
    }
    return this.backTex;
  }
  private faceTex(code: string): THREE.CanvasTexture {
    const tex = new THREE.CanvasTexture(drawCard(code));
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /**
   * Fly a card from `from` to `to`. `code` ⇒ face-up; otherwise face-down. `faceToward` ⇒ the card
   * hovers upright (with a flowing bob) facing that point; otherwise it lies flat on the felt.
   */
  deal(from: THREE.Vector3, to: THREE.Vector3, opts?: { dur?: number; code?: string; faceToward?: THREE.Vector3 }): void {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(CARD_W, CARD_H),
      new THREE.MeshBasicMaterial({ map: opts?.code ? this.faceTex(opts.code) : this.cardBack(), transparent: true, side: THREE.DoubleSide, depthWrite: false }),
    );
    mesh.position.copy(from);
    if (!opts?.faceToward) mesh.rotation.set(-Math.PI / 2, 0, 0); // lie flat on the felt
    mesh.renderOrder = 5;
    this.parent.add(mesh);
    this.phaseSeed += 1.7;
    this.cards.push({ mesh, from: from.clone(), to: to.clone(), faceToward: opts?.faceToward ? opts.faceToward.clone() : null, flying: true, t: 0, dur: opts?.dur ?? 700, phase: this.phaseSeed, restClock: 0 });
  }

  /** Remove every card (call on hand end / table clear). */
  clear(): void {
    for (const c of this.cards) this.dispose(c.mesh);
    this.cards.length = 0;
  }

  private dispose(mesh: THREE.Mesh): void {
    this.parent.remove(mesh);
    mesh.geometry.dispose();
    const mat = mesh.material as THREE.MeshBasicMaterial;
    if (mat.map && mat.map !== this.backTex) mat.map.dispose(); // per-card face textures; the back is shared
    mat.dispose();
  }

  tick(dtMs: number): void {
    this.clock += dtMs;
    for (const c of this.cards) {
      if (c.flying) {
        c.t += dtMs;
        const k = Math.min(1, c.t / c.dur);
        const e = 1 - (1 - k) * (1 - k); // ease-out
        c.mesh.position.lerpVectors(c.from, c.to, e);
        c.mesh.position.y += Math.sin(k * Math.PI) * 0.3; // arc up mid-flight
        if (c.faceToward) c.mesh.lookAt(c.faceToward.x, c.mesh.position.y, c.faceToward.z);
        if (k >= 1) { c.flying = false; c.restClock = 0; }
      } else if (c.faceToward) {
        // Resting in the air: a small bob that DECAYS to rest (a gentle settle, not a constant loop).
        c.restClock += dtMs;
        const decay = Math.exp(-c.restClock / 1300); // ~fully settled within ~3s
        const bob = Math.sin(c.restClock * 0.0045 + c.phase) * 0.009 * decay;
        c.mesh.position.set(c.to.x, c.to.y + bob, c.to.z);
        c.mesh.lookAt(c.faceToward.x, c.mesh.position.y, c.faceToward.z); // face the player, no wobble
      }
    }
  }
}
